import { useCallback, useEffect, useState } from 'react';
import PackagesApi from '../api/paquetes';

const useAvailableDates = (originId, destinationId) => {
  const [availableDates, setAvailableDates] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const normalizeId = useCallback((id) => {
    if (Array.isArray(id)) {
      return id.join(',');
    }
    return String(id);
  }, []);

  const getCacheKey = useCallback((originId, destinationId) => {
    return `availableDates_${originId}_${destinationId}`;
  }, []);

  const getCachedDates = useCallback(
    (originId, destinationId) => {
      if (!originId || !destinationId) return null;

      try {
        const cacheKey = getCacheKey(originId, destinationId);
        const cached = sessionStorage.getItem(cacheKey);
        if (cached) {
          const parsed = JSON.parse(cached);
          if (Date.now() - parsed.timestamp < 3600000) {
            return parsed.data;
          }
        }
      } catch (error) {
        console.warn('Error reading cached dates:', error);
      }
      return null;
    },
    [getCacheKey]
  );

  const cacheDates = useCallback(
    (originId, destinationId, dates) => {
      if (!originId || !destinationId || !dates) return;

      try {
        const cacheKey = getCacheKey(originId, destinationId);
        const cacheData = {
          data: dates,
          timestamp: Date.now(),
        };
        sessionStorage.setItem(cacheKey, JSON.stringify(cacheData));
      } catch (error) {
        console.warn('Error caching dates:', error);
      }
    },
    [getCacheKey]
  );

  const fetchAvailableDates = useCallback(async () => {
    // Solo resetear si ambos IDs están presentes pero son diferentes a los anteriores
    if (!originId || !destinationId) {
      // No resetear inmediatamente, mantener las fechas previas
      setIsLoading(false);
      setError(null);
      return;
    }

    const cachedDates = getCachedDates(originId, destinationId);
    if (cachedDates) {
      setAvailableDates(cachedDates);
      setIsLoading(false);
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Usar la misma función de normalización
      const normalizedOriginId = normalizeId(originId);
      const normalizedDestinationId = normalizeId(destinationId);

      const data = await PackagesApi.getAvailableDates({
        originId: normalizedOriginId,
        destinationId: normalizedDestinationId,
      });

      if (data && data.availableDates) {
        setAvailableDates(data.availableDates);
        cacheDates(originId, destinationId, data.availableDates);
      } else {
        setAvailableDates(null);
      }
    } catch (err) {
      console.error('Error fetching available dates:', err);
      setError(err.message || 'Error al obtener las fechas disponibles');
      setAvailableDates(null);
    } finally {
      setIsLoading(false);
    }
  }, [originId, destinationId, getCachedDates, cacheDates, normalizeId]);

  useEffect(() => {
    fetchAvailableDates();
  }, [fetchAvailableDates]);

  const refreshAvailableDates = useCallback(() => {
    // Limpiar cache antes de refrescar
    if (originId && destinationId) {
      try {
        const cacheKey = getCacheKey(originId, destinationId);
        sessionStorage.removeItem(cacheKey);
      } catch (error) {
        console.warn('Error clearing cache:', error);
      }
    }
    fetchAvailableDates();
  }, [fetchAvailableDates, originId, destinationId, getCacheKey]);

  const clearAvailableDates = useCallback(() => {
    setAvailableDates(null);
    setError(null);
    setIsLoading(false);
  }, []);

  return {
    availableDates,
    isLoading,
    error,
    refreshAvailableDates,
    clearAvailableDates,
  };
};

export default useAvailableDates;
